import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Upload, Check, Crown } from "lucide-react";
import fanApi from "@/lib/fanApi";
import { useFanAuth } from "@/context/FanAuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { resolveImg } from "./CommentMeta";

export default function AvatarPicker({ open, onOpenChange, onChanged }) {
  const { user, setUser } = useFanAuth();
  const [avatars, setAvatars] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) fanApi.get("/avatars").then((r) => setAvatars(r.data)).catch(() => {});
  }, [open]);

  const apply = (picture) => {
    setUser({ ...user, picture });
    onChanged?.();
    onOpenChange(false);
    toast.success("Avatar actualizado");
  };

  const run = async (fn) => {
    setSaving(true);
    try {
      apply((await fn()).data.picture);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el avatar");
    } finally {
      setSaving(false);
    }
  };

  const select = (id) => run(() => fanApi.put("/fan-auth/avatar", { avatar_id: id }));

  const upload = (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    run(() => fanApi.post("/fan-auth/avatar/upload", form));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-white/15 text-white max-w-md" data-testid="avatar-picker">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}>ELIGE TU AVATAR</DialogTitle>
        </DialogHeader>
        {avatars.length === 0 ? (
          <p className="text-gray-400 text-sm py-6 text-center">Pronto habrá avatares disponibles.</p>
        ) : (
          <div className="grid grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto p-1">
            {avatars.map((a) => {
              const active = user?.picture === a.image_url;
              return (
                <button
                  key={a.id}
                  disabled={saving}
                  onClick={() => select(a.id)}
                  className={`relative aspect-square rounded-full overflow-hidden border-4 transition-transform hover:scale-105 ${active ? "border-[#FFE500]" : "border-transparent"}`}
                  data-testid={`avatar-option-${a.id}`}
                >
                  <img src={resolveImg(a.image_url)} alt="" className="w-full h-full object-cover bg-black" />
                  {active && <Check className="absolute bottom-0 right-0 w-5 h-5 bg-[#FFE500] text-black rounded-full p-0.5" />}
                </button>
              );
            })}
          </div>
        )}
        {user?.is_subscriber && (
          <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-[#FFE500] text-black font-bold text-sm py-2.5 rounded-xl hover:bg-[#FFE500]/80 disabled:opacity-50"
              data-testid="avatar-upload-btn"
            >
              <Crown className="w-4 h-4" /> <Upload className="w-4 h-4" /> Subir mi propio avatar
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
